import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";

// Map URL prefix -> i18n key for page title.
// Order matters: longer/more specific prefixes first.
const PAGE_TITLE_MAP: Array<[string, string]> = [
  ["/journal/new", "journal.addEntry"],
  ["/journal", "journal.title"],
  ["/vouchers/payment", "vouchers.paymentTitle"],
  ["/vouchers/receipt", "vouchers.receiptTitle"],
  ["/assets", "assets.title"],
  ["/prepaid", "prepaid.title"],
  ["/cip", "cip.title"],
  ["/accruals", "accruals.title"],
  ["/cash-count", "cashCount.title"],
  ["/cash-custody", "cashCustody.title"],
  ["/asset-custody-transfer", "assetCustody.title"],
  ["/leases", "leases.title"],
  ["/reports/trial-balance", "reports.trialBalance"],
  ["/reports/ledger", "reports.ledger"],
  ["/reports/income-statement", "reports.incomeStatement"],
  ["/reports/balance-sheet", "reports.balanceSheet"],
  ["/reports", "reports.title"],
  ["/setup/accounts", "nav.accounts"],
  ["/setup/cost-centers", "nav.costCenters"],
  ["/setup/cash-denominations", "nav.cashDenominations"],
  ["/setup/fiscal-periods", "nav.fiscalPeriods"],
  
  ["/setup/taxes-zakat", "nav.taxesZakat"],
  ["/setup/document-numbering", "nav.documentNumbering"],
  ["/settings/company", "nav.companySettings"],
  ["/settings/members", "nav.members"],
  ["/companies/new", "companies.new"],
  ["/companies", "companies.title"],
  ["/profile", "nav.profile"],
  ["/auth", "auth.title"],
  ["/forgot-password", "auth.forgotPassword"],
  ["/reset-password", "auth.resetPassword"],
  ["/", "nav.dashboard"],
];

// Public marketing/help routes manage their own <title> via <SEO /> (Helmet).
const MARKETING_PREFIXES = ["/features", "/pricing", "/blog", "/help", "/contact", "/legal"];

function isMarketingPath(pathname: string) {
  const base = pathname.replace(/^\/(en|fr)(?=\/|$)/, "") || "/";
  return base === "/" || MARKETING_PREFIXES.some((p) => base === p || base.startsWith(`${p}/`));
}

export function usePageTitle() {
  const { pathname } = useLocation();
  const { t, i18n } = useTranslation();

  useEffect(() => {
    document.documentElement.lang = i18n.language;
    document.documentElement.dir = i18n.dir();

    if (isMarketingPath(pathname)) return;

    const match = PAGE_TITLE_MAP.find(([prefix]) =>
      prefix === "/" ? pathname === "/" : pathname.startsWith(prefix),
    );
    const key = match?.[1];
    const brand = t("app.name");
    let pageName = "";
    if (key) {
      const translated = t(key);
      if (translated && translated !== key) pageName = translated;
    }
    document.title = pageName ? `${brand} - ${pageName}` : brand;
  }, [pathname, i18n.language, t]);
}

